import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Trash2, LogOut, Inbox, Loader2, Download } from "lucide-react";
import { formatApiErrorDetail } from "@/pages/AdminPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "in_progress", "closed"];

const URGENCY_BADGE = {
  urgent: "border-gold/50 bg-gold/10 text-gold",
  standard: "border-white/15 bg-white/5 text-slate-300",
  audit: "border-cobalt/50 bg-cobalt/10 text-blue-300",
};

const STATUS_STYLE = {
  new: "text-gold",
  contacted: "text-blue-300",
  in_progress: "text-slate-200",
  closed: "text-slate-500",
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function AdminDashboard({ user, onLogout }) {
  const [enquiries, setEnquiries] = useState(null);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API}/admin/enquiries`, { withCredentials: true });
          setEnquiries(data);
          return;
        } catch (e2) {
          toast.error("Session expired — please log in again.");
          onLogout();
          return;
        }
      }
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load enquiries");
    } finally {
      setBusy(false);
    }
  }, [onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    const prev = enquiries;
    setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    try {
      await axios.patch(`${API}/admin/enquiries/${id}`, { status }, { withCredentials: true });
      toast.success(`Status set to ${status.replace("_", " ")}`);
    } catch (err) {
      setEnquiries(prev);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Status update failed");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/enquiries/export`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `astra-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Enquiry export downloaded");
    } catch (err) {
      toast.error("Export failed — please try again");
    } finally {
      setExporting(false);
    }
  };

  const remove = async (enq) => {
    if (!window.confirm(`Delete enquiry ${enq.reference}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/enquiries/${enq.id}`, { withCredentials: true });
      setEnquiries((list) => list.filter((e) => e.id !== enq.id));
      toast.success("Enquiry deleted");
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const total = enquiries ? enquiries.length : 0;
  const urgentCount = enquiries ? enquiries.filter((e) => e.urgency === "urgent").length : 0;
  const newCount = enquiries ? enquiries.filter((e) => e.status === "new").length : 0;

  return (
    <div className="min-h-screen bg-obsidian font-sans text-slate-50">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-obsidian/90 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img
              src="/logo-mark.png"
              alt="Astra Consult logo"
              className="h-10 w-10 rounded-lg border border-white/10 object-cover"
            />
            <div className="leading-none">
              <p className="font-display text-sm font-extrabold tracking-[0.08em] text-white">
                ASTRA COMMAND
              </p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.28em] text-gold">
                Enquiry triage
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 sm:block">
              {user?.email}
            </span>
            <button
              data-testid="admin-export-button"
              onClick={exportCsv}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-plate/60 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300 transition-all hover:border-gold/40 hover:text-gold"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              <span className="hidden sm:inline">Export CSV</span>
            </button>
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="grid h-10 w-10 place-items-center rounded-lg border border-white/10 bg-plate/60 text-slate-300 transition-all hover:border-gold/40 hover:text-gold"
              aria-label="Refresh enquiries"
            >
              <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={onLogout}
              className="flex items-center gap-2 rounded-lg border border-white/10 bg-plate/60 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-300 transition-all hover:border-red-500/40 hover:text-red-300"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          <div data-testid="stat-total" className="rounded-xl border border-white/10 bg-plate/60 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Total enquiries</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-white">{total}</p>
          </div>
          <div data-testid="stat-urgent" className="rounded-xl border border-gold/30 bg-gold/5 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Urgent / last-minute</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-gold">{urgentCount}</p>
          </div>
          <div data-testid="stat-new" className="rounded-xl border border-white/10 bg-plate/60 p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Awaiting triage</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-white">{newCount}</p>
          </div>
        </div>

        {enquiries === null ? (
          <div className="grid h-64 place-items-center">
            <Loader2 className="h-8 w-8 animate-spin text-gold" />
          </div>
        ) : enquiries.length === 0 ? (
          <div data-testid="admin-empty-state" className="mt-8 grid place-items-center rounded-2xl border border-dashed border-white/10 py-20 text-center">
            <Inbox className="h-10 w-10 text-slate-600" />
            <p className="mt-4 text-slate-400">No enquiries yet.</p>
            <p className="mt-1 text-sm text-slate-600">
              Submissions from the website form will appear here instantly.
            </p>
          </div>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
            <table data-testid="admin-enquiries-table" className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-plate/80 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                  <th className="px-4 py-4">Reference</th>
                  <th className="px-4 py-4">Received</th>
                  <th className="px-4 py-4">Client</th>
                  <th className="px-4 py-4">Need</th>
                  <th className="px-4 py-4">Urgency</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {enquiries.map((e) => (
                  <tr
                    key={e.id}
                    data-testid="admin-enquiry-row"
                    className={`border-b border-white/5 transition-colors hover:bg-white/[0.03] ${
                      e.urgency === "urgent" ? "bg-gold/[0.04]" : ""
                    }`}
                  >
                    <td className="px-4 py-4 align-top">
                      <span className="font-mono text-xs text-gold">{e.reference}</span>
                      <p
                        className="mt-1.5 max-w-[240px] truncate text-xs leading-relaxed text-slate-400"
                        title={e.message}
                      >
                        {e.message}
                      </p>
                    </td>
                    <td className="px-4 py-4 align-top font-mono text-xs text-slate-500">
                      {fmtDate(e.created_at)}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <p className="font-medium text-slate-100">{e.full_name}</p>
                      <p className="mt-0.5 text-xs text-slate-500">{e.company}</p>
                      <a href={`mailto:${e.email}`} className="mt-0.5 block text-xs text-blue-300 hover:underline">
                        {e.email}
                      </a>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <p className="text-xs text-slate-300">{e.project_type}</p>
                      <p className="mt-0.5 text-xs text-slate-600">{e.industry}</p>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <span
                        className={`inline-block rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.14em] ${
                          URGENCY_BADGE[e.urgency] || URGENCY_BADGE.standard
                        }`}
                      >
                        {e.urgency}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <select
                        data-testid={`admin-status-select-${e.reference}`}
                        value={e.status}
                        onChange={(ev) => setStatus(e.id, ev.target.value)}
                        className={`rounded-lg border border-white/10 bg-abyss/80 px-2.5 py-2 font-mono text-[10px] uppercase tracking-[0.12em] outline-none focus:border-gold/50 ${
                          STATUS_STYLE[e.status] || "text-slate-300"
                        }`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s.replace("_", " ")}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-4 text-right align-top">
                      <button
                        data-testid={`admin-delete-button-${e.reference}`}
                        onClick={() => remove(e)}
                        className="grid h-9 w-9 place-items-center rounded-lg border border-white/10 text-slate-500 transition-all hover:border-red-500/40 hover:text-red-300"
                        aria-label={`Delete enquiry ${e.reference}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
